import React, { createContext, useContext, useEffect } from 'react';
import posthog from 'posthog-js';

const PostHogContext = createContext(null);

const LocationIQApiKey = process.env.REACT_APP_LOCATION_IQ_API_KEY;

export const PostHogProvider = ({ children }) => {
  useEffect(() => {
    // Initialize PostHog
    posthog.init(
      process.env.REACT_APP_POSTHOG_API_KEY,
      {
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
        // Capture pageviews automatically
        capture_pageview: true,
        // Capture performance metrics
        capture_performance: true,
        // Enhanced persistence for anonymous user tracking
        persistence: 'localStorage+cookie',
        // Session recording (optional - can disable for privacy)
        session_recording: {
          recordCrossOriginIframes: false,
        },
        // Disable in development (optional)
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') {
            posthog.opt_out_capturing();
            console.log('PostHog disabled in development');
          } else {
            // Create or retrieve anonymous user ID for tracking returning users
            let anonId = localStorage.getItem('foodiecall_anon_id');
            if (!anonId) {
              anonId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              localStorage.setItem('foodiecall_anon_id', anonId);
            }
            
            // Identify the anonymous user to enable lifecycle tracking
            posthog.identify(anonId);
          }
        },
      }
    );

    return () => {
      // Cleanup on unmount
      posthog.reset();
    };
  }, []);

  return (
    <PostHogContext.Provider value={posthog}>
      {children}
    </PostHogContext.Provider>
  );
};

// Custom hook to use PostHog
export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    console.warn('usePostHog must be used within PostHogProvider');
    return posthog; // Return the global instance as fallback
  }
  return context;
};

// Helper function to get city/state from coordinates (without exposing exact location)
export const getLocationLabel = async (lat, lon) => {
  try {
    // Using a reverse geocoding service (you can use your LocationIQ key)
    const response = await fetch(
      `https://us1.locationiq.com/v1/reverse?key=${LocationIQApiKey}&lat=${lat}&lon=${lon}&format=json`
    );
    const data = await response.json();
    
    const city = data.address?.city || data.address?.town || data.address?.village || 'Unknown';
    const state = data.address?.state || '';
    
    return state ? `${city}, ${state}` : city;
  } catch (error) {
    console.error('Error getting location label:', error);
    return 'Unknown';
  }
};

export default posthog;